import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  Accordion,
  Alert,
  Badge,
  Button,
  Checkbox,
  Code,
  Container,
  Drawer,
  Group,
  Loader,
  MultiSelect,
  ScrollArea,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { IconAlertCircle, IconBook2 } from '@tabler/icons-react';

import { nanoid } from 'nanoid';

import { CapabilitiesSection, LogoPaper, PageTitle } from '@/components';
import {
  APPLICABLE_SUBJECT_TYPE_OPTIONS,
  APPLICABLE_SUBJECT_TYPES,
  normaliseApplicableSubjectType,
} from '@/constants';
import { useScopeComponents } from '@/hooks';
import { vnextApiClient } from '@/services/vnext-api';
import {
  BackendComponentSchema,
  DependencyValidationResponse,
  RenderingContextCatalogue,
  ScopeComponentDataType,
  ScopeComponentDependency,
} from '@/types';
import { leafFulfillment } from '@/utils';

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, '-')
    .replaceAll(/^-|-$/g, '');
}

interface FormValues {
  name: string;
  slug: string;
  description: string;
  defaultPrompt: string;
  dataType: ScopeComponentDataType;
  dependencySlugs: string[];
  /** The ticked subject types; empty means the component applies to every type. */
  applicableSubjectTypes: string[];
}

function getInitialFormValues(): FormValues {
  return {
    name: '',
    slug: '',
    description: '',
    defaultPrompt: '',
    dataType: 'text',
    dependencySlugs: [],
    applicableSubjectTypes: [],
  };
}

/** The stored `applicable_subject_types` list as the checkboxes show it:
 * `individual` reads as `person`, duplicates collapse, and every list —
 * `['vessel']`, `['company', 'vessel']` — round-trips as itself rather than
 * collapsing to "all" (which an edit would then save as null, undoing the
 * gating). */
function toApplicableSubjectTypes(types: string[] | null | undefined): string[] {
  const ticked = new Set((types ?? []).map(normaliseApplicableSubjectType));
  return APPLICABLE_SUBJECT_TYPES.filter((type) => ticked.has(type));
}

/** The checkboxes back to the wire value: none (or every type) ticked is
 * null, which applies to every subject type, including ones added later. */
function fromApplicableSubjectTypes(ticked: string[]): string[] | null {
  const chosen = APPLICABLE_SUBJECT_TYPES.filter((type) => ticked.includes(type));
  return chosen.length === 0 || chosen.length === APPLICABLE_SUBJECT_TYPES.length ? null : chosen;
}

export function ScopeComponentEditPage() {
  const { componentId } = useParams<{ componentId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isNew = !componentId;

  const { scopeComponents, getComponent, createComponent, updateComponent } = useScopeComponents({
    includeAll: true,
  });

  // For new components, the query param determines the type.
  // For existing components, we detect from dependencies after loading.
  const [isReportSection, setIsReportSection] = useState(
    isNew ? searchParams.get('type') !== 'data-source' : true
  );

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [renderingContext, setRenderingContext] = useState<RenderingContextCatalogue | null>(null);
  const [validationResult, setValidationResult] = useState<DependencyValidationResponse | null>(
    null
  );
  const [drawerOpened, drawerHandlers] = useDisclosure(false);

  const form = useForm<FormValues>({
    mode: 'controlled',
    initialValues: getInitialFormValues(),
    validate: {
      name: (value) => (!value.trim() ? 'Name is required' : null),
      slug: (value) => {
        if (!value.trim()) {
          return 'Slug is required';
        }
        if (!/^[a-z0-9-]+$/.test(value)) {
          return 'Slug must only contain lowercase letters, numbers, and hyphens';
        }
        return null;
      },
      defaultPrompt: (value, values) => {
        if (values.dependencySlugs.length > 0 && values.dataType !== 'image' && !value.trim()) {
          return 'Prompt is required when dependencies are set and data type is Text';
        }
        return null;
      },
    },
  });

  const loadConfig = useCallback(async () => {
    if (!componentId) {
      return;
    }
    try {
      setLoading(true);
      const component = await getComponent(componentId);
      const hasDependencies = component.dependencies.length > 0;
      setIsReportSection(hasDependencies);
      form.setValues({
        name: component.name,
        slug: component.slug,
        description: component.description ?? '',
        defaultPrompt: component.defaultPrompt ?? '',
        dataType: component.dataType ?? 'text',
        dependencySlugs: component.dependencies
          .filter(
            (d): d is { type: 'scope_component'; scope_component_slug: string } =>
              d.type === 'scope_component'
          )
          .map((d) => d.scope_component_slug),
        applicableSubjectTypes: toApplicableSubjectTypes(component.applicableSubjectTypes),
      });
      setSlugManuallyEdited(true);
    } catch {
      // Error handled by hook
    } finally {
      setLoading(false);
    }
  }, [componentId, getComponent]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    vnextApiClient
      .getRenderingContext()
      .then(setRenderingContext)
      .catch(() => {});
  }, []);

  const handleNameChange = (value: string) => {
    form.setFieldValue('name', value);
    if (!slugManuallyEdited) {
      form.setFieldValue('slug', slugify(value));
    }
  };

  // Build the dependency select options from all scope components (backend + selectable)
  const dependencyOptions = scopeComponents
    .filter((sc) => {
      // When editing, exclude self from dependency options
      if (!isNew && componentId) {
        return sc.id !== componentId;
      }
      return true;
    })
    .map((sc) => {
      // For data-source leaves, surface whether they're API-automated or manual
      // so a section author doesn't pick a manual source expecting automation.
      const suffix =
        sc.dependencies.length === 0
          ? leafFulfillment(sc) === 'automated'
            ? ' · Automated'
            : ' · Manual'
          : '';
      return { value: sc.slug, label: `${sc.name}${suffix}` };
    });

  // Resolve selected dependency slugs to their backend component schemas (if any)
  const selectedBackendSchemas: BackendComponentSchema[] = renderingContext
    ? form.values.dependencySlugs
        .map((slug) => renderingContext.backend_components.find((bc) => bc.slug === slug))
        .filter((bc): bc is BackendComponentSchema => bc !== undefined)
    : [];

  // Validate dependencies when they change
  const handleDependenciesChange = async (slugs: string[]) => {
    form.setFieldValue('dependencySlugs', slugs);
    setValidationResult(null);

    if (slugs.length === 0) {
      return;
    }

    const dependencies: ScopeComponentDependency[] = slugs.map((slug) => ({
      type: 'scope_component',
      scope_component_slug: slug,
    }));

    try {
      const result = await vnextApiClient.validateScopeComponentDependencies({
        dependencies,
        exclude_slug: isNew ? undefined : form.values.slug,
      });
      setValidationResult(result);
    } catch {
      // Validation is best-effort; errors will be caught on save
    }
  };

  const handleSubmit = async (values: FormValues) => {
    try {
      setSaving(true);

      const dependencies: ScopeComponentDependency[] = isReportSection
        ? values.dependencySlugs.map((slug) => ({
            type: 'scope_component',
            scope_component_slug: slug,
          }))
        : [];

      const applicableSubjectTypes = fromApplicableSubjectTypes(values.applicableSubjectTypes);

      if (isNew) {
        await createComponent({
          name: values.name,
          slug: values.slug,
          description: values.description || null,
          default_prompt: isReportSection ? values.defaultPrompt || null : null,
          data_type: isReportSection ? values.dataType : null,
          dependencies,
          applicable_subject_types: applicableSubjectTypes,
        });
      } else if (componentId) {
        await updateComponent(componentId, {
          name: values.name,
          description: values.description || null,
          default_prompt: isReportSection ? values.defaultPrompt || null : null,
          data_type: isReportSection ? values.dataType : null,
          dependencies,
          applicable_subject_types: applicableSubjectTypes,
        });
      }

      navigate(
        isReportSection ? '/admin/scope-components' : '/admin/scope-components?view=data-sources'
      );
    } catch {
      // Error handled by hook notifications
    } finally {
      setSaving(false);
    }
  };

  // Return URL: preserves the tab the user was on. Report Sections is the
  // default view so its param is omitted.
  const listHref = isReportSection
    ? '/admin/scope-components'
    : '/admin/scope-components?view=data-sources';

  const handleCancel = () => {
    navigate(listHref);
  };

  const typeLabel = isReportSection ? 'Report Section' : 'Data Source';

  if (loading) {
    return (
      <Stack gap="xl">
        <PageTitle
          title={isNew ? `Create ${typeLabel}` : `Edit ${typeLabel}`}
          subtitle="Loading..."
        />
        <Container size="md" px={0}>
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        </Container>
      </Stack>
    );
  }

  return (
    <Stack gap="xl">
      <PageTitle
        title={isNew ? `Create ${typeLabel}` : `Edit ${typeLabel}`}
        subtitle={
          isNew
            ? isReportSection
              ? 'Define a new report section with dependencies and a prompt'
              : 'Define a new data source for report sections'
            : `Editing: ${form.values.name}`
        }
      />

      <Container size="sm" px={0}>
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="lg">
            <LogoPaper>
              <Stack gap="md">
                <Title order={4}>General</Title>

                <TextInput
                  withAsterisk
                  label="Name"
                  placeholder="e.g. Risk Summary Table"
                  {...form.getInputProps('name')}
                  onChange={(e) => handleNameChange(e.currentTarget.value)}
                />

                <TextInput
                  withAsterisk
                  label="Slug"
                  placeholder="e.g. risk-summary-table"
                  description={
                    isNew
                      ? 'URL-safe identifier. Cannot be changed after creation.'
                      : 'Slug is immutable and cannot be changed.'
                  }
                  {...form.getInputProps('slug')}
                  disabled={!isNew}
                  onChange={(e) => {
                    if (isNew) {
                      setSlugManuallyEdited(true);
                      form.setFieldValue('slug', e.currentTarget.value);
                    }
                  }}
                />

                <TextInput
                  label="Description"
                  placeholder="Describe what this component renders"
                  {...form.getInputProps('description')}
                />

                <Stack gap={4}>
                  <Text size="sm" fw={500}>
                    Applies to
                  </Text>
                  <Text size="xs" c="dimmed">
                    Tick the subject types this component applies to; it is omitted for subjects of
                    any other type. Nothing ticked means it applies to every subject type.
                  </Text>
                  <Checkbox.Group {...form.getInputProps('applicableSubjectTypes')}>
                    <Group gap="md">
                      {APPLICABLE_SUBJECT_TYPE_OPTIONS.map((option) => (
                        <Checkbox key={option.value} value={option.value} label={option.label} />
                      ))}
                    </Group>
                  </Checkbox.Group>
                </Stack>
              </Stack>
            </LogoPaper>

            {!isReportSection && !isNew && componentId && (
              <CapabilitiesSection componentId={componentId} />
            )}

            {isReportSection && (
              <>
                <LogoPaper>
                  <Stack gap="md">
                    <Title order={4}>Dependencies</Title>
                    <Text size="sm" c="dimmed">
                      Choose which data sources this section needs. Backend components provide
                      structured data (e.g. sanctions matches, adverse media articles). Other report
                      sections provide their already-rendered markdown output.
                    </Text>

                    <MultiSelect
                      label="Component Dependencies"
                      placeholder="Select dependencies..."
                      data={dependencyOptions}
                      value={form.values.dependencySlugs}
                      onChange={handleDependenciesChange}
                      searchable
                      clearable
                    />

                    {validationResult && !validationResult.valid && (
                      <Alert
                        icon={<IconAlertCircle size={16} />}
                        color="red"
                        variant="light"
                        title="Dependency errors"
                      >
                        {validationResult.errors.map((err, _) => (
                          <Text key={nanoid()} size="sm">
                            {err}
                          </Text>
                        ))}
                      </Alert>
                    )}
                    <Select
                      label="Data Type"
                      description="How this section is rendered during synthesis. Text sections use an LLM with a prompt. Image sections bypass the LLM and display uploaded images directly."
                      data={[
                        { value: 'text', label: 'Text' },
                        { value: 'image', label: 'Image' },
                      ]}
                      value={form.values.dataType}
                      onChange={(value) =>
                        form.setFieldValue('dataType', (value as ScopeComponentDataType) ?? 'text')
                      }
                    />
                  </Stack>
                </LogoPaper>

                {form.values.dataType !== 'image' && (
                  <LogoPaper>
                    <Stack gap="md">
                      <Group justify="space-between" align="flex-start">
                        <div>
                          <Title order={4}>Default Prompt</Title>
                          <Text size="sm" c="dimmed" mt={4}>
                            Tell the LLM how to render this section — what format to use, what to
                            emphasise, and any rules to follow. The LLM will receive your prompt
                            along with the subject metadata and data from the dependencies you
                            selected above.
                          </Text>
                        </div>
                        <Button
                          variant="light"
                          size="xs"
                          leftSection={<IconBook2 size={14} />}
                          onClick={drawerHandlers.open}
                        >
                          Available data
                        </Button>
                      </Group>

                      <Textarea
                        label="Prompt"
                        placeholder="e.g. Render the adverse media findings as a markdown table with columns for Title, Risk Level, Source, and Date..."
                        minRows={8}
                        maxRows={20}
                        autosize
                        {...form.getInputProps('defaultPrompt')}
                      />

                      {selectedBackendSchemas.length > 0 && (
                        <Alert
                          variant="light"
                          color="blue"
                          title="Data available from your dependencies"
                        >
                          <Text size="sm" mb="xs">
                            Your selected backend dependencies will provide the following structured
                            data to the LLM alongside your prompt:
                          </Text>
                          {selectedBackendSchemas.map((schema) => (
                            <Text key={schema.slug} size="sm">
                              <Text span fw={500}>
                                {schema.name}
                              </Text>{' '}
                              — {schema.description}
                            </Text>
                          ))}
                        </Alert>
                      )}
                    </Stack>
                  </LogoPaper>
                )}
              </>
            )}

            <Group justify="flex-end">
              <Button variant="default" onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="submit" loading={saving}>
                {isNew ? `Create ${typeLabel}` : 'Save Changes'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Container>

      <RenderingContextDrawer
        opened={drawerOpened}
        onClose={drawerHandlers.close}
        context={renderingContext}
      />
    </Stack>
  );
}

function RenderingContextDrawer({
  opened,
  onClose,
  context,
}: Readonly<{
  opened: boolean;
  onClose: () => void;
  context: RenderingContextCatalogue | null;
}>) {
  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title="Available Data Reference"
      position="right"
      size="lg"
      padding="lg"
    >
      <ScrollArea h="calc(100vh - 80px)" offsetScrollbars>
        {!context ? (
          <Group justify="center" py="xl">
            <Loader size="sm" />
          </Group>
        ) : (
          <Stack gap="xl">
            <Stack gap="xs">
              <Text size="sm">
                When your component runs, the LLM receives a prompt built from four sections. Your{' '}
                <Text span fw={600}>
                  Default Prompt
                </Text>{' '}
                becomes the instructions section. The rest is assembled automatically from the
                subject being screened and the dependencies you selected.
              </Text>
              <Text size="sm" c="dimmed">
                Use this reference to understand what data you can refer to in your prompt.
              </Text>
            </Stack>

            <Stack gap="sm">
              <Title order={5}>How the prompt is assembled</Title>
              {context.prompt_template.sections.map((section) => (
                <Stack key={section.name} gap={2}>
                  <Group gap="xs">
                    <Badge size="sm" variant="light">
                      {section.name}
                    </Badge>
                    <Text size="xs" c="dimmed">
                      {section.source}
                    </Text>
                  </Group>
                  <Text size="sm" ml="sm">
                    {section.description}
                  </Text>
                </Stack>
              ))}
            </Stack>

            <Stack gap="sm">
              <Title order={5}>Subject metadata</Title>
              <Text size="sm" c="dimmed">
                {context.subject_metadata.description}
              </Text>
              {context.subject_metadata.fields.map((field) => (
                <Group key={field.name} gap="xs" wrap="nowrap" align="flex-start">
                  <Code style={{ flexShrink: 0 }}>{field.name}</Code>
                  <Stack gap={0}>
                    <Text size="sm">{field.description}</Text>
                    <Group gap="xs">
                      <Text size="xs" c="dimmed">
                        {field.type}
                      </Text>
                      <Badge
                        size="xs"
                        variant="light"
                        color={field.always_present ? 'green' : 'gray'}
                      >
                        {field.always_present ? 'always present' : 'optional'}
                      </Badge>
                    </Group>
                  </Stack>
                </Group>
              ))}
            </Stack>

            <Stack gap="sm">
              <Title order={5}>Backend component data</Title>
              <Text size="sm" c="dimmed">
                When you add a backend component as a dependency, its structured output is included
                as JSON in the prompt. Expand a component below to see the shape of data your prompt
                will receive.
              </Text>
              <Accordion variant="separated">
                {context.backend_components.map((comp) => (
                  <Accordion.Item key={comp.slug} value={comp.slug}>
                    <Accordion.Control>
                      <Stack gap={2}>
                        <Text size="sm" fw={500}>
                          {comp.name}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {comp.description}
                        </Text>
                      </Stack>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Code block>{JSON.stringify(comp.output_schema, null, 2)}</Code>
                    </Accordion.Panel>
                  </Accordion.Item>
                ))}
              </Accordion>
            </Stack>

            {context.selectable_components.length > 0 && (
              <Stack gap="sm">
                <Title order={5}>Other selectable components</Title>
                <Text size="sm" c="dimmed">
                  If you depend on another selectable component, its rendered markdown output is
                  included in your prompt under a heading with its slug.
                </Text>
                {context.selectable_components.map((comp) => (
                  <Stack key={comp.slug} gap={2}>
                    <Text size="sm" fw={500}>
                      {comp.name}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {comp.description ?? 'No description'} — outputs {comp.output_type}
                    </Text>
                  </Stack>
                ))}
              </Stack>
            )}
          </Stack>
        )}
      </ScrollArea>
    </Drawer>
  );
}
